// Help topics for the simulated assistant. Each topic has keywords used for matching and a
// plain-text answer (blank lines separate paragraphs, lines starting with "1." are steps).
export const topics = [
  {
    id: 'add-building',
    title: 'Add a building',
    keywords: ['add building', 'new building', 'create building', 'real estate', 'register building', 'building'],
    answer: `To add a new building:
1. Open "Buildings" in the left menu.
2. Click "Add New Real Estate" (the + button, top right).
3. Fill in the building details (name, address and so on) and choose the registration year, which is the first year the building has data. The year filters on the reports start from that year.
4. Press "Submit".

Once it is saved, select it from the list to see its units and control panel.`
  },
  {
    id: 'add-unit',
    title: 'Add a unit',
    keywords: ['add unit', 'new unit', 'create unit', 'add property', 'new property', 'apartment', 'unit', 'flat', 'shop', 'office'],
    answer: `To add a unit to a building:
1. Open "Buildings" and select the building.
2. Open "Units" in the left menu.
3. Click "Add New Unit" (the + button, top right).
4. Enter the unit number, floor, type (flat, shop or office), square meters, monthly fee and the month it starts from.
5. Enter the owner's full name, email and phone number.
6. Press "Submit".

After saving, use the "Send Activation" icon on the unit's row to invite the owner to the platform.`
  },
  {
    id: 'activation',
    title: 'Invite an owner / send activation',
    keywords: ['activation', 'activate', 'invite', 'invitation', 'owner access', 'owner login', 'resend', 'confirm account'],
    answer: `To give an owner access:
1. Open "Units" for the building.
2. On the owner's row, click the "Send Activation" icon (it shows "Resend Activation" if one was already sent).
3. The owner receives an email and sets their own password from the link.

A tick shows on the row once the owner has activated the account.`
  },
  {
    id: 'record-expense',
    title: 'Record an expense',
    keywords: ['expense', 'add expense', 'record expense', 'bill', 'supplier bill', 'additional expense', 'monthly expense', 'cost'],
    answer: `To record an expense:
1. Open the building's "Control Panel".
2. Click the + button to open the form.
3. Under "Expenses", choose "Additional Expenses" for a one-off cost, or "Monthly Expenses" if the building uses monthly expenses.
4. Pick the expense type and provider, enter the amount and date, and distribute it between the units if asked.
5. Press "Submit".

You can review them later under "Additional Expenses" in the left menu.`
  },
  {
    id: 'pay-expense',
    title: 'Pay a supplier',
    keywords: ['pay supplier', 'pay now', 'pay expense', 'payment to supplier', 'pay provider', 'pay bill', 'unpaid expenses'],
    answer: `To pay a supplier for an expense:
1. Open the building's "Control Panel" and click the + button.
2. Choose "Pay Now" (under "Principals").
3. Select the expense, enter the amount, date, payment type and reference.
4. Press "Submit".

The expense moves from "Unpaid Expenses" to "Expenses Paid" in Graph Reports.`
  },
  {
    id: 'collect-payment',
    title: 'Collect a payment from an owner',
    keywords: ['collect', 'payment', 'collect payment', 'receive payment', 'owner paid', 'income', 'uncollected', 'monthly fee', 'record payment'],
    answer: `To record a payment received from an owner:
1. Open the building's "Control Panel" and click the + button.
2. Under "Uncollected Income" choose "Collect Monthly Income" (monthly fees) or "Collect Shared Payment" (shared expenses).
3. Select the unit, enter the amount, date, payment type and reference.
4. Press "Submit".

A receipt can then be sent from the unit's payments page.`
  },
  {
    id: 'expense-type',
    title: 'Add an expense type or provider',
    keywords: ['expense type', 'expense types', 'provider', 'providers', 'supplier', 'suppliers', 'vendor', 'category'],
    answer: `Expense types and providers are managed from the left menu:
1. Open the building, then "More" if the menu is collapsed.
2. Choose "Add New Expense Type" to create a category (and its VAT percentage), or "Providers" to add a supplier.
3. Link a provider to the expense types it serves.

They then appear in the expense forms.`
  },
  {
    id: 'recurring',
    title: 'Recurring expenses',
    keywords: ['recurring', 'repeat', 'repeating', 'every month', 'standing'],
    answer: `To set up a recurring expense (for example cleaning every month):
1. Open "Recurring Expenses" in the left menu.
2. Click the + button.
3. Choose the expense type and provider, the amount, and the start and end month.
4. Press "Submit".

The expense is created automatically for each month in the range.`
  },
  {
    id: 'reports',
    title: 'Reports and graphs',
    keywords: ['report', 'reports', 'graph', 'graphs', 'total income', 'total expenses', 'cash balance', 'statistics', 'chart', 'export pdf', 'pdf'],
    answer: `For an overview of a building's finances:
1. Open "Graph Reports" in the left menu to see Total Income, Total Expenses and Cash Balance. Use the period selector to switch between the current and last year.
2. Open "Additional Reports" for the breakdown of additional expenses by month.
3. Use the export button (top right of Graph Reports) to save the page as a PDF.`
  },
  {
    id: 'messages',
    title: 'Send a message to owners',
    keywords: ['message', 'messages', 'notification', 'notifications', 'notify', 'announcement', 'email owners', 'send message', 'chat'],
    answer: `To message owners:
1. Open "Notifications" in the left menu.
2. Start a new message and choose the participants (one or more owners).
3. Enter a subject and your message, then send.

Replies appear in the same thread.`
  },
  {
    id: 'invoices',
    title: 'Invoices, receipts and statements',
    keywords: ['invoice', 'invoices', 'receipt', 'receipts', 'statement', 'statements', 'send invoice', 'send receipt', 'credit note'],
    answer: `Per-unit documents are available from the "Units" list:
1. Open "Units" for the building.
2. On a unit's row, use the action icons to open its payments, receipts, invoices or statement.
3. From those pages you can send an invoice or receipt by email.

Credit notes can be issued from the payments and expenses pages.`
  },
  {
    id: 'committee',
    title: 'Committee members',
    keywords: ['committee', 'board', 'chairman', 'treasurer', 'secretary'],
    answer: `To manage the building committee:
1. Open "Committee" in the left menu.
2. Click the + button to add a member and choose their position.
3. Use the edit or delete icons on a row to change or remove a member.`
  },
  {
    id: 'cash-deposit',
    title: 'Cash deposits',
    keywords: ['cash deposit', 'deposit', 'cash desk', 'cash'],
    answer: `Cash held for the building appears as "Cash Desk" in Graph Reports. Open the Cash Deposit report from Graph Reports to see how the balance is made up.`
  },
  {
    id: 'account',
    title: 'Account, password and settings',
    keywords: ['password', 'change password', 'forgot password', 'account', 'profile', 'settings', 'logo', 'logout', 'log out', 'sign out'],
    answer: `Account options are under your initials (top right):
1. Click your initials to open the menu.
2. "Account" lets you update your details and change your password.
3. "Settings" lets you set the name, address and logo used on invoices.
4. "Logout" signs you out.

If you forgot your password, use "Forgot Password?" on the login page.`
  },
  {
    id: 'users',
    title: 'Manage users (administrators)',
    keywords: ['users', 'add user', 'new user', 'management company', 'administrator', 'admin', 'licence', 'license'],
    answer: `Administrators can manage users from the initials menu (top right) by choosing "Users". There you can search, add and edit management accounts.`
  }
];

export const greeting = `Hello! I can help you find your way around the platform. Ask me how to do something, for example "how do I add a unit?", or pick one of the suggestions below.`;

export const suggestions = [
  'How do I add a building?',
  'How do I add a unit?',
  'How do I record an expense?',
  'How do I collect a payment?',
  'Where are the reports?'
];

export const fallback = `I am not sure I understood that. Try asking about one of these: adding a building or unit, recording expenses, collecting or paying payments, reports, messages, invoices and receipts, the committee, or your account.`;

export const smallTalk = [
  { pattern: /^(hi|hello|hey|good (morning|afternoon|evening))\b/i, answer: 'Hello! What would you like to know how to do?' },
  { pattern: /\b(thanks|thank you|cheers)\b/i, answer: 'You are welcome! Let me know if there is anything else.' },
  { pattern: /\b(bye|goodbye)\b/i, answer: 'Goodbye! I am here whenever you need help.' }
];
